import { useEffect, useMemo, useState } from 'react';
import type { MaxsoldBidContextDto } from '@jordan/types';
import { Alert, Button, Modal, Spinner } from '@jordan/ui';
import { ApiError, apiGet, apiPost } from '../../lib/api';
import { formatMoney } from './maxsoldSearchUtils';

interface BidBoxProps {
  lotId: number;
  title: string;
  currency: string;
  closeTime: string;
  /** True when Maxsold account credentials are configured on the server. */
  configured: boolean;
  /** Buyer's premium (%) — shown in the confirm dialog so the true cost is clear. */
  surchargePercent: number;
  /** Deep link to the lot on MaxSold (kept as a secondary link). */
  externalUrl: string;
  now: number;
  /** Called whenever fresh bid context loads or a bid is placed, so the page can sync the current bid. */
  onContext?: (ctx: MaxsoldBidContextDto) => void;
}

/**
 * The in-app bid control on the lot page. Loads the lot's bid context (current / minimum / the
 * viewer's own max), lets the user enter a maximum bid, validates it against the minimum (and, when
 * they've already bid, requires a raise), and confirms before placing a real bid on MaxSold.
 */
export function MaxsoldBidBox({
  lotId,
  title,
  currency,
  closeTime,
  configured,
  surchargePercent,
  externalUrl,
  now,
  onContext,
}: BidBoxProps) {
  const [ctx, setCtx] = useState<MaxsoldBidContextDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const ended = closeTime ? Date.parse(closeTime) <= now : false;

  // Prefill: only when leading (winning) do we seed the current max to raise it. Outbid or new →
  // seed the new minimum bid and treat it like a regular bid.
  const seedAmount = (c: MaxsoldBidContextDto) => {
    const leading = c.userHasBid && c.isWinning && c.proxyBid != null;
    setAmount(leading ? String(c.proxyBid) : c.minimumBid != null ? String(c.minimumBid) : '');
  };

  useEffect(() => {
    if (!configured) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    apiGet<MaxsoldBidContextDto>(`/api/maxsold/lot/${lotId}/bid`)
      .then((c) => {
        setCtx(c);
        seedAmount(c);
        onContext?.(c); // sync the page's current bid to the fresh (authed) value
      })
      .catch(() => setError('Could not load bid details.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotId, configured]);

  // Client-side validation mirroring the rules the requirement describes.
  const validation = useMemo(() => {
    if (!ctx) return { value: NaN, message: null as string | null };
    const value = Number(amount);
    if (!amount.trim() || !Number.isFinite(value) || value <= 0) {
      return { value, message: 'Enter a bid amount.' };
    }
    if (ctx.minimumBid != null && value < ctx.minimumBid) {
      return { value, message: `Bid must be at least ${formatMoney(ctx.minimumBid)}.` };
    }
    // Raise-only applies only while leading; once outbid it's a fresh bid governed by the minimum.
    if (ctx.userHasBid && ctx.isWinning && ctx.proxyBid != null && value <= ctx.proxyBid) {
      return { value, message: `Enter more than your current max of ${formatMoney(ctx.proxyBid)} to raise it.` };
    }
    return { value, message: null };
  }, [ctx, amount]);

  const submit = async () => {
    if (!ctx || validation.message) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await apiPost<MaxsoldBidContextDto>(`/api/maxsold/lot/${lotId}/bid`, { amount: validation.value });
      setCtx(updated);
      seedAmount(updated);
      setSuccess(`Bid placed — your maximum is now ${formatMoney(updated.proxyBid ?? validation.value)}.`);
      onContext?.(updated);
    } catch (e: unknown) {
      const body = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
      setError(body?.message ?? 'Could not place that bid.');
    } finally {
      setSubmitting(false);
      setConfirming(false);
    }
  };

  if (!configured) {
    return (
      <Alert variant="info" icon="ph-info">
        Connect your MaxSold account (set <code>MAXSOLD_EMAIL</code> / <code>MAXSOLD_PASSWORD</code> on the server) to
        bid from here.
        {externalUrl && (
          <>
            {' '}
            <a href={externalUrl} target="_blank" rel="noopener noreferrer">
              View on MaxSold
            </a>
          </>
        )}
      </Alert>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner />
      </div>
    );
  }

  if (ended) {
    return (
      <Alert variant="secondary" icon="ph-clock-countdown">
        This auction has ended.
        {externalUrl && (
          <>
            {' '}
            <a href={externalUrl} target="_blank" rel="noopener noreferrer">
              View on MaxSold
            </a>
          </>
        )}
      </Alert>
    );
  }

  // Leading = you're the current high bidder (raise-only). Outbid = you had a bid but were beaten →
  // treat like a fresh bid. New = you've never bid.
  const leading = !!ctx?.userHasBid && !!ctx?.isWinning;
  const outbid = !!ctx?.userHasBid && !ctx?.isWinning;

  return (
    <div>
      {success && (
        <Alert variant="success" icon="ph-check-circle" dismissible>
          {success}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" icon="ph-warning" dismissible>
          {error}
        </Alert>
      )}

      {leading && ctx?.proxyBid != null && (
        <div className="mb-2 fs-sm">
          <span className="badge bg-success bg-opacity-10 text-success me-2">Winning</span>
          Your current maximum is <span className="fw-semibold">{formatMoney(ctx.proxyBid)}</span>. Raise it below.
        </div>
      )}
      {outbid && (
        <div className="mb-2 fs-sm">
          <span className="badge bg-danger bg-opacity-10 text-danger me-2">Outbid</span>
          {ctx?.proxyBid != null ? (
            <>
              Your previous max of <span className="fw-semibold">{formatMoney(ctx.proxyBid)}</span> was beaten — bid
              again below.
            </>
          ) : (
            <>You&apos;ve been outbid — bid again below.</>
          )}
        </div>
      )}

      <label htmlFor="maxsold-bid" className="form-label">
        {leading ? 'New maximum bid' : 'Your maximum bid'}
      </label>
      <div className="input-group">
        <span className="input-group-text">$</span>
        <input
          id="maxsold-bid"
          type="number"
          inputMode="decimal"
          className={`form-control${validation.message && amount.trim() ? ' is-invalid' : ''}`}
          min={ctx?.minimumBid ?? 0}
          step={1}
          value={amount}
          disabled={submitting}
          onChange={(e) => {
            setAmount(e.target.value);
            setSuccess(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !validation.message) setConfirming(true);
          }}
        />
        <span className="input-group-text">{currency}</span>
      </div>

      <div className="form-text">
        {ctx?.minimumBid != null ? (
          <>
            Minimum bid: <span className="fw-semibold">{formatMoney(ctx.minimumBid)}</span>
          </>
        ) : (
          'Enter your maximum bid.'
        )}
      </div>
      {validation.message && amount.trim() && <div className="text-danger fs-sm mt-1">{validation.message}</div>}

      <Button
        variant="primary"
        className="w-100 mt-3"
        icon="ph-gavel"
        disabled={!!validation.message || submitting}
        onClick={() => setConfirming(true)}
      >
        {leading ? 'Update bid' : 'Place bid'}
      </Button>

      {externalUrl && (
        <div className="text-center mt-2">
          <a href={externalUrl} target="_blank" rel="noopener noreferrer" className="fs-sm text-muted">
            <i className="ph-arrow-square-out me-1" />
            View on MaxSold
          </a>
        </div>
      )}

      <Modal
        open={confirming}
        onClose={() => (submitting ? undefined : setConfirming(false))}
        title="Confirm your bid"
        centered
        footer={
          <>
            <Button variant="light" disabled={submitting} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="primary" icon="ph-gavel" disabled={submitting} onClick={submit}>
              {submitting ? 'Placing…' : 'Confirm bid'}
            </Button>
          </>
        }
      >
        <p className="mb-2">
          Place a maximum bid of <span className="fw-semibold">{formatMoney(validation.value)}</span> on “{title}”?
        </p>
        <p className="text-muted fs-sm mb-0">
          This is a real, binding bid on MaxSold. If you win, a {surchargePercent}% buyer&apos;s premium applies — up to{' '}
          <span className="fw-semibold">
            {formatMoney(Math.round(validation.value * (1 + surchargePercent / 100)))}
          </span>{' '}
          in total.
        </p>
      </Modal>
    </div>
  );
}
